import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import DosenTooltip from '../../../../../../../../components/ui/DosenTooltip';
import HistoryIcon from '@mui/icons-material/History';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { CtaButton } from '../components/SettingsUI';
import { useCourseVersioning } from './CourseVersioningContext';
import { formatDateTime, fromEpochSeconds } from '../../../../../../../../utils/formatDate';

interface Props {
  expanded?: boolean;
  onToggleExpanded?: () => void;
}

/** "just now", "2 min ago", "1 hr ago", else the clock time. */
function savedAgo(unixSeconds: number, nowMs: number): string {
  const diff = Math.max(0, Math.floor(nowMs / 1000) - unixSeconds);
  if (diff < 45) return 'just now';
  const min = Math.round(diff / 60);
  if (min < 60) return `${min} min ago`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  return formatDateTime(fromEpochSeconds(unixSeconds));
}

/**
 * Designer header under the draft model (PRD A4):
 * Version History · status · full screen · Publish Update.
 */
export function VersioningHeaderControls({ expanded = false, onToggleExpanded }: Props) {
  const v = useCourseVersioning();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  if (!v) return null;

  const previewing = Boolean(v.previewEntry);
  const status = v.hasChanges
    ? { text: 'Unpublished changes', color: '#F79420' }
    : { text: `Live · v${v.liveVersion}`, color: '#4CAF84' };
  const saveText =
    v.saveState === 'saving'
      ? 'Saving draft…'
      : v.saveState === 'pending'
      ? 'Unsaved edits…'
      : v.saveState === 'error'
      ? v.readOnly
        ? 'Draft is read-only'
        : 'Couldn’t save draft · retrying'
      : v.savedAt
      ? `All changes saved · ${savedAgo(v.savedAt, now)}`
      : 'Draft up to date';
  const publishTip = previewing
    ? 'Resume your current draft to publish'
    : v.readOnly
    ? `${v.lockHolder?.name || 'Another admin'} is editing this program`
    : v.hasChanges
    ? ''
    : `No changes since v${v.liveVersion}`;

  return (
    <>
      <DosenTooltip title="Version History">
        <IconButton
          onClick={() => v.setPanelOpen(!v.panelOpen)}
          size="small"
          aria-label="Version History"
          aria-pressed={v.panelOpen}
          sx={{ color: v.panelOpen ? '#F79420' : '#FDFDFD' }}
        >
          <HistoryIcon sx={{ fontSize: 26 }} />
        </IconButton>
      </DosenTooltip>
      {/* The save state shows on hover; only a failed save stays on screen. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <DosenTooltip title={saveText}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'Inter',
              color: status.color,
              whiteSpace: 'nowrap',
              cursor: 'default'
            }}
          >
            {status.text}
          </Typography>
        </DosenTooltip>
        {v.saveState === 'error' && (
          <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: '#F16063', whiteSpace: 'nowrap' }}>
            {saveText}
          </Typography>
        )}
      </Box>
      {onToggleExpanded && (
        <DosenTooltip title={expanded ? 'Exit full screen' : 'Full screen'}>
          <IconButton onClick={onToggleExpanded} size="small" sx={{ color: '#FDFDFD' }}>
            {expanded ? <FullscreenExitIcon sx={{ fontSize: 26 }} /> : <FullscreenIcon sx={{ fontSize: 26 }} />}
          </IconButton>
        </DosenTooltip>
      )}
      <DosenTooltip title={publishTip}>
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
          <CtaButton
            onClick={() => v.openPublish()}
            disabled={previewing || v.readOnly || !v.hasChanges || v.saveState === 'saving'}
          >
            Publish Update
          </CtaButton>
        </Box>
      </DosenTooltip>
    </>
  );
}
